import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { 
  Play, Pause, SkipForward, SkipBack, Volume2, VolumeX,
  GraduationCap, CheckCircle2, Loader2, X, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

import { API_BASE_URL as API } from '../utils/apiConfig';

// Training steps with actual element selectors for each dashboard
const DASHBOARD_TRAINING = {
  'command-center': {
    title: 'Command Center Walkthrough',
    steps: [
      {
        id: 'welcome',
        title: 'Welcome',
        selector: null,
        position: 'center',
        script: `Welcome to the Fundle Brain Command Center! This is your central hub for monitoring all brands at a glance. Think of it as your mission control - where you can see the health of every brand in your portfolio instantly. Let me walk you through each section.`
      },
      {
        id: 'brand-cards',
        title: 'Brand Overview Cards',
        selector: '[data-testid="brand-snapshot-card"], .brand-card, [class*="brand-snapshot"]',
        fallbackSelector: '.grid > div:first-child',
        position: 'right',
        script: `These are your brand cards - your quick health check for each brand. Each card shows the brand name, total members, revenue, and most importantly the repeat ratio. The color coding helps you instantly identify which brands need attention. Green means healthy, red needs focus.`
      },
      {
        id: 'members-metric',
        title: 'Total Members',
        selector: '[data-testid="members-metric"], [class*="total-members"], .stat-card:nth-child(1)',
        position: 'bottom',
        script: `This shows your total loyalty members. This is your enrolled customer base - people who've joined your loyalty program. Compare this with your total customers to calculate enrollment rate. A healthy program has 60 to 80 percent enrollment.`
      },
      {
        id: 'one-timers',
        title: 'One-Timers vs Repeat',
        selector: '[data-testid="one-timers"], [class*="one-timer"], [class*="cohort"]',
        position: 'bottom',
        script: `Here's the crucial split - One-Timers versus Repeat customers. One-Timers have purchased only once. They're your conversion opportunity. Repeat customers are your loyal base generating 3 to 5 times more lifetime value. The ideal benchmark is 40% or higher repeat rate.`
      },
      {
        id: 'revenue',
        title: 'Revenue Analysis', 
        selector: '[data-testid="revenue-metric"], [class*="revenue"], .stat-card:nth-child(2)',
        position: 'bottom',
        script: `This is your total revenue from loyalty members. But here's the insight - compare it with non-member revenue to see your Loyalty Contribution percentage. A healthy program contributes 65 to 75 percent of total revenue.`
      },
      {
        id: 'repeat-ratio',
        title: 'Repeat Ratio',
        selector: '[data-testid="repeat-ratio"], [class*="repeat-ratio"], [class*="progress"]',
        position: 'top',
        script: `The Repeat Ratio is your most important health metric. It's calculated as Repeat Customers divided by Total Customers. Industry benchmarks vary - Fashion retail averages 35 to 45 percent, Grocery can reach 60 to 70 percent. If below average, focus on Day 7 follow-up campaigns.`
      },
      {
        id: 'filters',
        title: 'Using Filters',
        selector: '[data-testid="date-filter"], [class*="filter"], select, [class*="dropdown"]',
        position: 'bottom',
        script: `Use these filters to slice your data. Lifetime shows all-time performance. Year-to-date helps track annual goals. Custom ranges are perfect for campaign analysis. All metrics update across the dashboard when you change filters.`
      },
      {
        id: 'actions',
        title: 'Taking Action',
        selector: null,
        position: 'center',
        script: `Now you're ready to take action! Identify brands with repeat ratio below 35 percent - they need attention. Look for high one-timer counts with low revenue - conversion opportunities. Click any brand card to dive deeper. That's your Command Center mastery complete!`
      }
    ]
  },
  'executive-dashboard': {
    title: 'Executive Dashboard Walkthrough',
    steps: [
      {
        id: 'welcome',
        title: 'Welcome',
        selector: null,
        position: 'center',
        script: `Welcome to the Executive Dashboard - your strategic command center for deep loyalty analytics. This dashboard shows the full picture: revenue contribution, customer cohorts, active member tracking, and revenue at risk.`
      },
      {
        id: 'kpi-cards',
        title: 'Key Performance Indicators',
        selector: '[data-testid="kpi-card"], [class*="kpi"], .grid > div',
        position: 'bottom',
        script: `These KPI cards are your executive summary. Each shows a critical metric with trend indicators. Green arrows mean improvement, red means decline. Click any card to drill down into detailed analysis with AI-powered insights.`
      },
      {
        id: 'loyalty-contribution',
        title: 'Loyalty Revenue Contribution',
        selector: '[data-testid="kpi-loyalty_contribution"], [class*="loyalty-contribution"]',
        position: 'right',
        script: `Loyalty Revenue Contribution shows what percentage of total revenue comes from loyalty members. A healthy range is 65 to 75 percent. Below 50 percent means you need more enrollment focus. Above 80 percent is excellent but ensure you're also acquiring new customers.`
      },
      {
        id: 'active-members',
        title: 'Active Members',
        selector: '[data-testid="kpi-active_members"], [class*="active-members"]',
        position: 'right',
        script: `Active Members shows customers who transacted in the last 90 days. Compare with total members to get your activation rate. A healthy rate is 25 to 35 percent. Below 20 percent signals engagement problems. These active members are your best upselling candidates.`
      },
      {
        id: 'cohort-chart',
        title: 'Cohort Contribution',
        selector: '[data-testid="cohort-chart"], [class*="cohort"], canvas, [class*="chart"]',
        position: 'left',
        script: `This chart shows how different customer segments contribute to revenue. Champions with 21 plus visits typically generate 35 percent of revenue despite being only 5 percent of customers. One-Timers might be 45 percent of your base but contribute just 5 to 10 percent of revenue.`
      },
      {
        id: 'revenue-at-risk',
        title: 'Revenue at Risk',
        selector: '[data-testid="revenue-at-risk"], [class*="at-risk"], [class*="churn"]',
        position: 'top',
        script: `Revenue at Risk shows potential loss from customers showing churn signals. It's bucketed by inactivity: 90 to 180 days is recoverable, 180 to 365 is high risk, beyond 365 is critical. This directly informs your win-back budget. Prioritize the 90 to 180 day bucket.`
      }
    ]
  }
};

// Spotlight overlay component
function Spotlight({ targetRect, padding = 12 }) {
  if (!targetRect) return null;
  
  const spotlightStyle = {
    position: 'fixed',
    top: targetRect.top - padding,
    left: targetRect.left - padding,
    width: targetRect.width + padding * 2,
    height: targetRect.height + padding * 2,
    boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.75)',
    borderRadius: '12px',
    border: '3px solid #8B5CF6',
    zIndex: 9998,
    pointerEvents: 'none',
    transition: 'all 0.4s ease-out'
  };

  return <div style={spotlightStyle} />;
}

// Main Interactive Walkthrough Component
export default function InteractiveWalkthrough({ dashboardId = 'command-center', onClose }) {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [targetRect, setTargetRect] = useState(null);
  const [audioProgress, setAudioProgress] = useState(0);
  
  const audioRef = useRef(null);
  const audioCache = useRef({});
  
  const training = DASHBOARD_TRAINING[dashboardId];
  const currentStep = training?.steps?.[currentStepIndex];
  const totalSteps = training?.steps?.length || 0;

  // Find and highlight element
  const highlightElement = useCallback(() => {
    if (!currentStep) return;
    
    // Remove previous highlight
    setTargetRect(null);
    
    if (!currentStep.selector) {
      // Center position for welcome/closing steps
      setTargetRect(null);
      return;
    }

    // Try to find element with selector
    let element = document.querySelector(currentStep.selector);
    
    // Try fallback selector if main not found
    if (!element && currentStep.fallbackSelector) {
      element = document.querySelector(currentStep.fallbackSelector);
    }

    if (element) {
      const rect = element.getBoundingClientRect();
      setTargetRect(rect);
      
      // Scroll element into view
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [currentStep]);

  // Highlight on step change
  useEffect(() => {
    const timer = setTimeout(highlightElement, 300);
    return () => clearTimeout(timer);
  }, [currentStepIndex, highlightElement]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => highlightElement();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [highlightElement]);

  // Generate audio for step
  const loadAudio = useCallback(async (stepId) => {
    const cacheKey = `${dashboardId}_${stepId}`;
    if (audioCache.current[cacheKey]) {
      return audioCache.current[cacheKey];
    }

    setIsLoading(true);
    try {
      // Use the training API to generate audio
      const step = training.steps.find(s => s.id === stepId);
      if (!step) return null;

      const response = await fetch(`${API}/api/training/generate-audio`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: step.script,
          voice: 'nova'
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        const audioUrl = `data:audio/mpeg;base64,${data.audio_base64}`;
        audioCache.current[cacheKey] = audioUrl;
        return audioUrl;
      }
    } catch (error) {
      console.error('Error loading audio:', error);
    } finally {
      setIsLoading(false);
    }
    return null;
  }, [dashboardId, training]);

  // Play current step
  const playStep = async () => {
    if (!currentStep) return;

    const audioUrl = await loadAudio(currentStep.id);
    if (audioUrl && audioRef.current) {
      audioRef.current.src = audioUrl;
      audioRef.current.muted = isMuted;
      try {
        await audioRef.current.play();
        setIsPlaying(true);
      } catch (e) {
        console.error('Audio play failed:', e);
      }
    }
  };

  // Audio event handlers
  const handleAudioEnded = () => {
    setIsPlaying(false);
    setAudioProgress(100);
    
    // Auto-advance after delay
    if (currentStepIndex < totalSteps - 1) {
      setTimeout(() => {
        setCurrentStepIndex(prev => prev + 1);
        setAudioProgress(0);
      }, 1500);
    }
  };

  const handleAudioTimeUpdate = () => {
    if (audioRef.current && audioRef.current.duration) {
      const progress = (audioRef.current.currentTime / audioRef.current.duration) * 100;
      setAudioProgress(progress);
    }
  };

  // Navigation
  const nextStep = () => {
    if (audioRef.current) audioRef.current.pause();
    setIsPlaying(false);
    if (currentStepIndex < totalSteps - 1) {
      setCurrentStepIndex(prev => prev + 1);
      setAudioProgress(0);
    }
  };

  const prevStep = () => {
    if (audioRef.current) audioRef.current.pause();
    setIsPlaying(false);
    if (currentStepIndex > 0) {
      setCurrentStepIndex(prev => prev - 1);
      setAudioProgress(0);
    }
  };

  const togglePlay = () => {
    if (isPlaying) {
      audioRef.current?.pause();
      setIsPlaying(false);
    } else {
      playStep();
    }
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
    if (audioRef.current) {
      audioRef.current.muted = !isMuted;
    }
  };

  const handleClose = () => {
    if (audioRef.current) audioRef.current.pause();
    onClose?.();
  };

  if (!training) return null;

  return createPortal(
    <>
      {/* Hidden audio element */}
      <audio 
        ref={audioRef}
        onEnded={handleAudioEnded}
        onTimeUpdate={handleAudioTimeUpdate}
      />

      {/* Spotlight overlay */}
      <Spotlight targetRect={targetRect} />

      {/* Control Panel - Fixed at bottom */}
      <div 
        className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[10000] w-full max-w-2xl px-4"
        style={{ pointerEvents: 'auto' }}
      >
        <div className="bg-gray-900/95 backdrop-blur-xl rounded-2xl border border-violet-500/30 shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-violet-500/20">
                <GraduationCap className="w-5 h-5 text-violet-400" />
              </div>
              <div>
                <h3 className="font-semibold text-white text-sm">{training.title}</h3>
                <p className="text-xs text-gray-400">Step {currentStepIndex + 1} of {totalSteps}</p>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={handleClose} className="text-gray-400 hover:text-white">
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Step Progress */}
          <div className="px-5 py-2 border-b border-white/5">
            <div className="flex gap-1">
              {training.steps.map((step, idx) => (
                <button
                  key={step.id}
                  onClick={() => {
                    if (audioRef.current) audioRef.current.pause();
                    setIsPlaying(false);
                    setCurrentStepIndex(idx);
                    setAudioProgress(0);
                  }}
                  className={`flex-1 h-1.5 rounded-full transition-all ${
                    idx < currentStepIndex 
                      ? 'bg-violet-500' 
                      : idx === currentStepIndex 
                        ? 'bg-violet-400' 
                        : 'bg-gray-700'
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="px-5 py-4">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-8 h-8 rounded-full bg-violet-500/20 flex items-center justify-center shrink-0">
                <span className="text-violet-400 font-bold text-sm">{currentStepIndex + 1}</span>
              </div>
              <div>
                <h4 className="font-medium text-white mb-1">{currentStep?.title}</h4>
                <p className="text-sm text-gray-300 leading-relaxed line-clamp-3">
                  {currentStep?.script}
                </p>
              </div>
            </div>

            {/* Audio Progress */}
            <Progress value={audioProgress} className="h-1 mb-4" />

            {/* Controls */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={prevStep}
                  disabled={currentStepIndex === 0}
                  className="text-gray-400 hover:text-white"
                >
                  <SkipBack className="w-5 h-5" />
                </Button>
                
                <Button 
                  onClick={togglePlay}
                  disabled={isLoading}
                  className="w-28 bg-violet-600 hover:bg-violet-700 text-white"
                >
                  {isLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : isPlaying ? (
                    <><Pause className="w-4 h-4 mr-2" /> Pause</>
                  ) : (
                    <><Play className="w-4 h-4 mr-2" /> Play</>
                  )}
                </Button>
                
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={nextStep}
                  disabled={currentStepIndex === totalSteps - 1}
                  className="text-gray-400 hover:text-white"
                >
                  <SkipForward className="w-5 h-5" />
                </Button>
                
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={toggleMute}
                  className="text-gray-400 hover:text-white"
                >
                  {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                </Button>
              </div>

              {currentStepIndex === totalSteps - 1 ? (
                <Button onClick={handleClose} className="bg-green-600 hover:bg-green-700 text-white">
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  Finish
                </Button>
              ) : (
                <Button variant="ghost" onClick={nextStep} className="text-violet-400 hover:text-violet-300">
                  Skip <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}

// Hook to launch walkthrough from any dashboard
export function useWalkthrough() {
  const [isActive, setIsActive] = useState(false);
  const [dashboardId, setDashboardId] = useState(null);

  const startWalkthrough = (id) => {
    setDashboardId(id);
    setIsActive(true);
  };

  const stopWalkthrough = () => {
    setIsActive(false);
    setDashboardId(null);
  };

  return {
    isActive,
    dashboardId,
    startWalkthrough,
    stopWalkthrough,
    WalkthroughComponent: isActive ? (
      <InteractiveWalkthrough dashboardId={dashboardId} onClose={stopWalkthrough} />
    ) : null
  };
}

// Button to start walkthrough
export function WalkthroughButton({ dashboardId, className }) {
  const [showWalkthrough, setShowWalkthrough] = useState(false);

  return (
    <>
      <Button 
        variant="outline" 
        size="sm"
        onClick={() => setShowWalkthrough(true)}
        className={className}
      >
        <GraduationCap className="w-4 h-4 mr-2" />
        Start Training
      </Button>
      
      {showWalkthrough && (
        <InteractiveWalkthrough 
          dashboardId={dashboardId} 
          onClose={() => setShowWalkthrough(false)} 
        />
      )}
    </>
  );
}
